import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, usePayments, useTogglePayment } from "../lib/hooks";
import { formatMoney, monthRange, occurrencesInRange, todayIso } from "../lib/finance";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { ChevronLeft, ChevronRight, Zap, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { usePaywall } from "../components/Paywall";

export default function MarkPaidPage() {
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";
  const stickersOn = user?.prefs?.stickers_enabled ?? true;
  const { data: bills = [] } = useBills();
  const { data: payments = [] } = usePayments();
  const toggle = useTogglePayment();

  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const paywall = usePaywall();
  const gotoMonth = (delta) => {
    const target = new Date(cursor.y, cursor.m + delta, 1);
    const now = new Date(); const curThis = new Date(now.getFullYear(), now.getMonth(), 1);
    if (target.getTime() !== curThis.getTime() && !paywall.isPaid) { paywall.gateQuota("Historical & future months"); return; }
    setCursor({ y: target.getFullYear(), m: target.getMonth() });
  };
  const { start, end } = monthRange(cursor.y, cursor.m);

  const paidSet = useMemo(
    () => new Set(payments.filter((p) => p.paid).map((p) => `${p.bill_id}_${p.occurrence_date}`)),
    [payments]
  );

  const occurrences = useMemo(() => {
    const list = [];
    for (const b of bills) {
      for (const d of occurrencesInRange(b.first_due_date, b.frequency, start, end)) {
        list.push({ bill: b, date: d, key: `${b.bill_id}_${d}` });
      }
    }
    return list.sort((a, b) => a.date.localeCompare(b.date));
  }, [bills, start, end]);

  const byWeek = useMemo(() => {
    const weeks = {};
    for (const o of occurrences) {
      const d = new Date(o.date);
      const day = d.getDay();
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - day);
      const label = `Week of ${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
      (weeks[label] ||= []).push(o);
    }
    return weeks;
  }, [occurrences]);

  const monthLabel = start.toLocaleString("default", { month: "long", year: "numeric" });
  const today = todayIso();

  const setPaid = (o, paid) => {
    toggle.mutate({ bill_id: o.bill.bill_id, occurrence_date: o.date, paid },
      { onSuccess: () => {
          if (paid && stickersOn) toast.success(`Paid! ${o.bill.name} · ${formatMoney(o.bill.amount, currency)} ${["💮","✨","🍀","🎉"][Math.floor(Math.random()*4)]}`);
        } });
  };

  const markAllPastDue = () => {
    let n = 0;
    for (const o of occurrences) {
      if (o.date <= today && !paidSet.has(o.key)) { toggle.mutate({ bill_id: o.bill.bill_id, occurrence_date: o.date, paid: true }); n++; }
    }
    toast.success(`Marked ${n} past-due occurrence${n===1?"":"s"} paid`);
  };
  const resetMonth = () => {
    if (!confirm("Reset all paid marks for this month?")) return;
    let n = 0;
    for (const o of occurrences) {
      if (paidSet.has(o.key)) { toggle.mutate({ bill_id: o.bill.bill_id, occurrence_date: o.date, paid: false }); n++; }
    }
    toast.success(`Cleared ${n} paid mark${n===1?"":"s"}`);
  };

  const totalPaid = occurrences.filter((o) => paidSet.has(o.key)).reduce((s, o) => s + Number(o.bill.amount || 0), 0);
  const totalDue = occurrences.filter((o) => !paidSet.has(o.key)).reduce((s, o) => s + Number(o.bill.amount || 0), 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="mark-paid-page">
      {paywall.Modal}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Mark as Paid</h1>
          <p className="text-sm text-muted-foreground mt-1">Every occurrence is independent — mark week 1 without marking the rest.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={()=>gotoMonth(-1)} data-testid="paid-month-prev"><ChevronLeft size={16}/></Button>
          <div className="font-display text-lg font-bold px-2" data-testid="paid-month-label">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={()=>gotoMonth(1)} data-testid="paid-month-next"><ChevronRight size={16}/></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Paid so far</div>
          <div className="tabular text-2xl font-bold text-[hsl(var(--paid-text))]" data-testid="paid-total">{formatMoney(totalPaid, currency)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Remaining</div>
          <div className="tabular text-2xl font-bold text-[hsl(var(--due-text))]" data-testid="due-total">{formatMoney(totalDue, currency)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex flex-col justify-center gap-2">
          <Button variant="outline" size="sm" onClick={markAllPastDue} data-testid="bulk-mark-past-due"><Zap size={14} className="mr-1"/> Mark all past-due paid</Button>
          <Button variant="outline" size="sm" onClick={resetMonth} data-testid="bulk-reset-month"><RotateCcw size={14} className="mr-1"/> Reset month</Button>
        </CardContent></Card>
      </div>

      <div className="space-y-6">
        {Object.keys(byWeek).length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-16 border border-dashed rounded-xl" data-testid="paid-empty">
            No bill occurrences this month.
          </div>
        )}
        {Object.entries(byWeek).map(([week, items]) => (
          <div key={week}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">{week}</div>
            <div className="rounded-xl border border-border bg-card divide-y">
              {items.map((o) => {
                const isPaid = paidSet.has(o.key);
                const isOverdue = !isPaid && o.date < today;
                return (
                  <div key={o.key} className="p-4 flex items-center gap-4" data-testid={`occurrence-row-${o.bill.bill_id}-${o.date}`}>
                    <Checkbox
                      checked={isPaid}
                      onCheckedChange={(v) => setPaid(o, !!v)}
                      data-testid={`occurrence-checkbox-${o.bill.bill_id}-${o.date}`}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{o.bill.name} <span className="text-xs text-muted-foreground">· {o.bill.category}</span></div>
                      <div className="text-xs text-muted-foreground tabular">
                        {new Date(o.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        {isOverdue && <span className="ml-2 text-[hsl(var(--overdue-text))] font-bold">OVERDUE</span>}
                      </div>
                    </div>
                    <div className={`tabular font-bold ${isPaid ? "line-through text-muted-foreground" : "text-[hsl(var(--due-text))]"}`}>
                      {formatMoney(o.bill.amount, currency)}
                    </div>
                    {isPaid && <span className="text-xs font-black text-[hsl(var(--paid-text))] border border-[hsl(var(--paid-border))] rounded px-1.5 -rotate-6" aria-hidden>PAID</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
